import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import parentService from '../services/parentService';

// Query keys
const parentKeys = {
  all: ['parents'],
  allParents: () => [...parentKeys.all, 'all-parents'],
  sentRequests: () => [...parentKeys.all, 'sent-requests'],
  receivedRequests: () => [...parentKeys.all, 'received-requests'],
  search: (username) => [...parentKeys.all, 'search', username],
};

// Get all parents of current player
export const useAllParentsOfPlayer = () => {
  return useQuery({
    queryKey: parentKeys.allParents(),
    queryFn: parentService.getAllParentsOfPlayer,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Get all sent requests
export const useAllSentRequests = () => {
  return useQuery({
    queryKey: parentKeys.sentRequests(),
    queryFn: parentService.getAllSentRequestsToAddParent,
    staleTime: 30 * 1000, // 30 seconds (requests change frequently)
  });
};

// Get all received requests
export const useAllReceivedRequests = () => {
  return useQuery({
    queryKey: parentKeys.receivedRequests(),
    queryFn: parentService.getAllReceivedRequestsToAddParent,
    staleTime: 30 * 1000, // 30 seconds
  });
};

// Search for parent by username
export const useSearchParentByUsername = (username) => {
  return useQuery({
    queryKey: parentKeys.search(username),
    queryFn: () => parentService.searchForParentOrChildByUsername(username),
    enabled: !!username && username.length > 2, // Only search if username has more than 2 characters
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// Send request from player to add parent
export const useSendRequestToAddParent = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ parentUsername, roles }) => 
      parentService.sendRequestFromPlayerToAddParent(parentUsername, roles),
    onSuccess: () => {
      // Invalidate and refetch sent requests
      queryClient.invalidateQueries({ queryKey: parentKeys.sentRequests() });
    },
    onError: (error) => {
      console.error('Error sending parent request:', error);
    },
  });
};

// Delete sent request
export const useDeleteSentRequest = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (requestId) => 
      parentService.deleteRequestFromPlayerToAddParent(requestId),
    onSuccess: () => {
      // Invalidate and refetch sent requests
      queryClient.invalidateQueries({ queryKey: parentKeys.sentRequests() });
    },
    onError: (error) => {
      console.error('Error deleting sent request:', error);
    },
  });
};

// Approve or reject received request
export const useApproveOrRejectRequest = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ requestId, status }) => 
      parentService.approveOrRejectRequestToAddParent(requestId, status),
    onSuccess: () => {
      // Invalidate and refetch both received requests and all parents
      queryClient.invalidateQueries({ queryKey: parentKeys.receivedRequests() });
      queryClient.invalidateQueries({ queryKey: parentKeys.allParents() });
    },
    onError: (error) => {
      console.error('Error handling request approval/rejection:', error);
    },
  });
};

// Remove parent relationship
export const useRemoveParent = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (parentId) => 
      parentService.deleteParentFromUser(parentId),
    onSuccess: () => {
      // Invalidate and refetch all parents
      queryClient.invalidateQueries({ queryKey: parentKeys.allParents() });
    },
    onError: (error) => {
      console.error('Error removing parent:', error);
    },
  });
};

// Get all players of current parent (for parent users)
export const useAllPlayersOfParent = () => {
  return useQuery({
    queryKey: [...parentKeys.all, 'all-players'],
    queryFn: parentService.getAllPlayersOfParent,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export { parentKeys }; 